import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty, cloneDeep, get } from 'lodash';
import ReviewSpaceRentalTable from '../../elements-tables/review-space-rental-table';
import TextButton from '../../elements-buttons/text-button';
import { CTAButton } from 'shared-components/buttons/cta-button';
import AddOnsTable from '../../app-main/components/reduxForms/fieldSets/AddOnsTable/add-ons-table';
import PriceTable from '../../elements-tables/price-table';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { BookingDetailsReview } from './BookingDetailsReview';
import { Link } from 'react-router';
import { normalizedContractsShape } from '../../app-bookings-request/forms/bookingUtil';
import { BookingUpdateSummary } from '../../elements-summaries/booking-update-summary/BookingUpdateSummary';

const ReviewGuestUpdateGV = ({
  priceBreakdown,
  pendingContracts,
  currentContracts,
  booking,
  profile,
  cancelAction,
  toggleModal,
  whichModal,
  iosWebView,
  closeIOSModal,
  intl
}) => {
  const {
    cleaningFee,
    currency,
    discounts,
    spaceRentalSubtotal,
    spaceRentalTotal,
    itemsArray,
    addOnsTotal,
    total,
    guestFee,
    guestPayment
  } = priceBreakdown;

  const activityDetailBooking = cloneDeep(booking);
  if (!isEmpty(activityDetailBooking)) {
    activityDetailBooking.payment = {};
    activityDetailBooking.list_price = {};
    activityDetailBooking.payout = {};
    activityDetailBooking.view_as = 'GUEST';
    activityDetailBooking.listing = get(
      pendingContracts,
      'spaceRentalContract.meta.listing',
      {}
    );
    activityDetailBooking.customOfferStep = 'review';
    activityDetailBooking.attendees = booking.number_of_guests;
  }

  const masterContract = get(pendingContracts, 'masterContract', {});
  const guestAmounts = get(masterContract, 'calculations.guest', {});
  const displaySplitPayments = get(
    masterContract,
    'split_payment_checkout',
    false
  );
  const splitPaymentChargeDate = get(
    masterContract,
    'split_payment_charge_date',
    ''
  );

  const guestSplitPaymentFee = get(guestAmounts, 'split_payment.fee', 0);
  const guestSplitPayment = get(guestAmounts, 'split_payment.parts[0]', 0);
  const guestSplitPaymentWithoutFee = get(
    guestAmounts,
    'split_payment.parts[1]',
    0
  );
  const guestSplitPaymentTotal = get(guestAmounts, 'split_payment.total', 0);
  const bookingCreatedDate = get(booking, 'date_created', '');

  return (
    <div
      className='ReviewBookingUpdateMain ps-row'
      data-testing-id='review-booking-container-guest-view'
    >
      <div className='margin-md-top col-xs-12'>
        <TextButton
          id='back-to-message-button'
          buttonText={intl.formatMessage({
            id: 'inbox_review_booking_update.review_guest_update_GV.message',
            defaultMessage: 'Message'
          })}
          icon={<div className='custom-arrow left' />}
          iconPosition='left'
          classNames='margin-md-top'
          onClick={closeIOSModal || cancelAction}
        />
      </div>

      <div className={classnames('header col-xs-12', { iosWebView })}>
        <h3>
          <FormattedMessage
            id='inbox_review_booking_update.review_guest_update_GV.view_requested_update'
            defaultMessage='View Requested Update'
          />
        </h3>
        <p className='text-base margin-xl-bottom'>
          <FormattedMessage
            id='inbox_review_booking_update.review_guest_update_GV.view_requested_descr'
            defaultMessage='View the requested updates. These updates are currently waiting for host approval'
          />
        </p>
      </div>

      <div className='col-md-7 col-sm-12 left-column margin-xl-bottom'>
        <BookingDetailsReview
          booking={activityDetailBooking}
          alcoholConsumed={activityDetailBooking?.alcohol_consumed}
        />
        <hr className='full-width' />
        <ReviewSpaceRentalTable
          cleaningFee={cleaningFee}
          subtotal={spaceRentalSubtotal}
          total={spaceRentalTotal}
          currency={currency}
          discounts={discounts}
          masterContract={masterContract}
        />

        {!isEmpty(itemsArray) && (
          <AddOnsTable
            isReviewPage
            addOnsTotal={addOnsTotal}
            itemsArray={itemsArray}
            contract={pendingContracts.spaceRentalContract}
            masterContract={pendingContracts.masterContract}
            ssoId={profile.SSOid}
            currency={currency}
            toggleModal={toggleModal}
            whichModal={whichModal}
            guestView
          />
        )}

        <PriceTable
          currency={currency}
          spaceRentalTotal={spaceRentalTotal}
          addOnsTotal={addOnsTotal}
          total={total[profile.SSOid]}
          processingFee={guestFee}
          priceTotal={guestPayment}
          discounts={discounts}
          itemsArray={itemsArray}
          splitPaymentFee={guestSplitPaymentFee}
          splitPaymentAmount={guestSplitPayment}
          splitPaymentAmountWithoutFee={guestSplitPaymentWithoutFee}
          splitPaymentTotal={guestSplitPaymentTotal}
          splitPaymentChargeDate={splitPaymentChargeDate}
          bookingCreatedDate={bookingCreatedDate}
          masterContract={masterContract}
        />

        <div className='cta-buttons flex space-between margin-xl-top'>
          <CTAButton
            onClickAction={closeIOSModal || cancelAction}
            buttonText={intl.formatMessage({
              id: 'inbox_review_booking_update.review_guest_update_GV.done',
              defaultMessage: 'Done'
            })}
            classNameString='done-button btn btn-default'
            isDisabled={false}
          />
        </div>
      </div>

      <div className='col-md-5 col-sm-12'>
        <BookingUpdateSummary
          pendingContracts={pendingContracts}
          currentContracts={currentContracts}
          profile={profile}
          intl={intl}
        />
        {iosWebView ? (
          <CTAButton
            buttonText={intl.formatMessage({
              id: 'inbox.see_current_details.view_original_receipt',
              defaultMessage: 'View Original Receipt'
            })}
            onClickAction={() => toggleModal('CurrentDetailsContainerModal')}
            classNameString='btn btn-default full-width margin-xl-top'
            isDisabled={false}
          />
        ) : (
          <Link to='current-details' target='_blank'>
            <CTAButton
              buttonText={intl.formatMessage({
                id: 'inbox.see_current_details.view_original_receipt',
                defaultMessage: 'View Original Receipt'
              })}
              onClickAction={() => {}}
              classNameString='btn btn-default full-width margin-xl-top'
              isDisabled={false}
            />
          </Link>
        )}
      </div>
    </div>
  );
};

ReviewGuestUpdateGV.propTypes = {
  booking: PropTypes.object,
  priceBreakdown: PropTypes.object,
  currentContracts: normalizedContractsShape,
  pendingContracts: normalizedContractsShape,
  cancelAction: PropTypes.func,
  toggleModal: PropTypes.func,
  profile: PropTypes.object,
  iosWebView: PropTypes.bool,
  closeIOSModal: PropTypes.func,
  intl: intlShape
};

export default injectIntl(ReviewGuestUpdateGV);
